if (!customElements.get("po-account-form-wrapper")) {
  customElements.define("po-account-form-wrapper", class extends HTMLElement {
      connectedCallback() {
          this.form = this.querySelector("form");
          this.submitButtons = this.querySelectorAll("[type='submit']");
          
          this.actionUrl = this.getAttribute("data-action-url");
          this.apiUrl = "";
          this.apiToken = "";
          this.processing = false;
          // capture original values so we can build dynamic update payloads
          this.originalEmail = this.form?.querySelector('[name="email"]')?.value || this.form?.querySelector('[name="customer[email]"]')?.value || null;
          this.originalPhone = this.form?.querySelector('[name="phone"]')?.value || this.form?.querySelector('[name="customer[phone]"]')?.value || null;
          this.originalBirthday = this.form?.querySelector('[name="birthday"]')?.value || null;
          const smsConsentField = this.form?.querySelector('[name="sms_consent"]');
          const mailMarketingField = this.form?.querySelector('[name="customer[accepts_marketing]"]');
          this.originalSmsConsent = smsConsentField ? !!smsConsentField.checked : false;
          this.originalMailMarketingConsent = mailMarketingField ? !!mailMarketingField.checked : false;

          this.form?.addEventListener("submit", this.submitHandler);

          // After registration, birthday is stored as tag "birth:YYYY-MM-DD"; sync to metafield on first account load
          this.syncBirthDateFromTagIfNeeded();

          // In Personal Information, profile cannot be updated until phone is entered and valid
          this.updateSubmitButtonsByPhone();
          const phoneField = this.form?.querySelector('[name="phone"]');
          if (phoneField) {
              phoneField.addEventListener("input", () => this.updateSubmitButtonsByPhone());
              phoneField.addEventListener("change", () => this.updateSubmitButtonsByPhone());
          }
      }

      /**
       * Enables or disables submit buttons and shows/hides the error message below the phone field
       * based on phone validity. In Personal Information section, user must enter a valid phone number.
       */
      updateSubmitButtonsByPhone() {
          const phoneField = this.form?.querySelector('[name="phone"]');
          const phoneErrorEl = this.form?.querySelector('#phone-error');
          if (!phoneField) return;
          const phoneValue = (phoneField.value || "").trim();
          const isValid = phoneValue !== "" && this.isValidPhone(phoneValue);

          this.submitButtons?.forEach((btn) => {
              btn.disabled = !isValid;
              btn.setAttribute("aria-disabled", isValid ? "false" : "true");
          });

          if (phoneErrorEl) {
              const requiredMsg = this.dataset.phoneRequiredMessage || "Please enter a phone number to update your profile.";
              if (phoneValue === "") {
                  phoneErrorEl.textContent = requiredMsg;
                  phoneErrorEl.classList.remove("tw-hidden");
                  phoneErrorEl.classList.add("po-error-text");
                  phoneField.classList.add("tw-error-border");
                  phoneField.setAttribute("aria-invalid", "true");
                  phoneField.closest("po-text-field")?.classList.add("tw-po-textfield-error");
              } else if (!this.isValidPhone(phoneValue)) {
                  phoneErrorEl.textContent = phoneErrorEl.dataset.errorMessage || requiredMsg;
                  phoneErrorEl.classList.remove("tw-hidden");
                  phoneErrorEl.classList.add("po-error-text");
                  phoneField.classList.add("tw-error-border");
                  phoneField.setAttribute("aria-invalid", "true");
                  phoneField.closest("po-text-field")?.classList.add("tw-po-textfield-error");
              } else {
                  phoneErrorEl.textContent = "";
                  phoneErrorEl.classList.add("tw-hidden");
                  phoneErrorEl.classList.remove("po-error-text");
                  phoneField.classList.remove("tw-error-border");
                  phoneField.removeAttribute("aria-invalid");
                  phoneField.closest("po-text-field")?.classList.remove("tw-po-textfield-error");
              }
          }
      }

      /**
       * If customer has birth tag but metafield facts.birth_date is empty (e.g. just registered),
       * call Boomi to persist the birth date to the metafield so it shows in the profile.
       */
      async syncBirthDateFromTagIfNeeded() {
          const birthFromTag = this.dataset.birthDateFromTag?.trim();
          const metafieldEmpty = this.dataset.birthMetafieldEmpty === "true";
          if (!birthFromTag || !metafieldEmpty) return;

          const customerGid = this.form?.querySelector('[name="customer_gid"]')?.value;
          const token = sessionStorage.getItem("customerAccessToken") ||
              localStorage.getItem("customerAccessToken") ||
              (typeof window !== "undefined" && (window.customerAccessToken || window.CUSTOMER_ACCESS_TOKEN)) ||
              null;
          if (!customerGid || !token) return;

          const boomiUrl = this.dataset.boomiEnv;
          if (!boomiUrl?.trim()) return;

          const formData = new FormData(this.form);
          const fields = {
              customer_gid: customerGid,
              birthday: birthFromTag,
              sms_consent: formData.get("sms_consent") === "on",
              accepts_marketing_email: formData.get("customer[accepts_marketing]") === "on"
          };
          try {
              await this.updateSmsConsent(fields, token, { forceBirthDate: true });
          } catch (e) {
              console.warn("[syncBirthDateFromTag] Sync failed:", e);
          }
      }

      disconnectedCallback() {
          this.form?.removeEventListener("submit", this.submitHandler);
      }

      validateField(field, validator, errorMessageElement) {
        const errorMessage = errorMessageElement.dataset.errorMessage;

        if (!validator(field.value.trim())) {
            errorMessageElement.textContent = errorMessage;
            field.classList.add('tw-error-border');
            field.closest('po-text-field')?.classList.add('tw-po-textfield-error');
            errorMessageElement.classList.remove('tw-hidden');
            errorMessageElement.classList.add('po-error-text');
            field.setAttribute("aria-invalid", "true");
            return false;
        } else {
            errorMessageElement.textContent = '';
            field.classList.remove('tw-error-border');
            field.closest('po-text-field')?.classList.remove('tw-po-textfield-error');
            errorMessageElement.classList.add('tw-hidden');
            errorMessageElement.classList.remove('po-error-text');
            field.removeAttribute("aria-invalid");
            return true;
        }
      }

      isValidPhone(value) {
        return /^\+\d{5,}$/.test(value.trim());
      }

      normalizePhone(value) {
          return (value || "").trim().replace(/\s+/g, "");
      }

      normalizeDate(value) {
          return (value || "").trim();
      }

      toBooleanConsent(value) {
          return value === true || value === "on" || value === "true" || value === 1 || value === "1";
      }

      submitHandler = (event) => {
          const formData = new FormData(this.form);
          const formFields = Object.fromEntries(formData.entries());

          // Clear previous form-level API error so phone-error is only used for phone validation
          const formErrorEl = this.form.querySelector('#account-form-error');
          if (formErrorEl) {
              formErrorEl.textContent = '';
              formErrorEl.classList.add('tw-hidden');
          }

          // Try multiple places for the customer access token: sessionStorage (preferred),
          // then localStorage, then any global variable that other scripts might set.
          const customerAccessToken = sessionStorage.getItem("customerAccessToken") ||
                                      localStorage.getItem("customerAccessToken") ||
                                      (window && (window.customerAccessToken || window.CUSTOMER_ACCESS_TOKEN)) ||
                                      null;

          if (!this.apiUrl || this.apiUrl.trim() === "") {
              this.apiUrl = formData.get("storefront_url");
              this.apiToken = formData.get("storefront_token");
          }
          
          event.preventDefault();
          event.stopPropagation();

          if (this.form.elements["customer[tags]"]) {
              this.form.elements["customer[tags]"].value = this.getCustomerTags(formFields);
          }

          if (!customerAccessToken) {
              console.error("Customer access token not found in sessionStorage/localStorage/window vars");

              // Redirect to login page and include a return_to parameter so the user comes back
              // to the account summary after sign in. dataset.loginRoute is expected to be
              // the account login URL (e.g. /account/login).
              try {
                  const loginBase = this.dataset.loginRoute || '/account/login';
                  const returnTo = encodeURIComponent(this.actionUrl || window.location.pathname + window.location.search || window.location.pathname);
                  const sep = loginBase.includes('?') ? '&' : '?';
                  document.location.href = `${loginBase}${sep}return_to=${returnTo}`;
              } catch (e) {
                  // Fallback simple redirect
                  document.location.href = this.dataset.loginRoute || '/account/login';
              }

              return;
          }

                    const phoneField = this.form.querySelector('[name="phone"]');
                    const phoneErrorEl = this.form.querySelector('#phone-error');

                    // In Personal Information, phone is always required to update the profile.
                    const phoneValue = phoneField?.value?.trim() || '';
                    let isPhoneValid = true;

                    if (phoneValue === '') {
                        if (phoneErrorEl) {
                            const requiredMsg = this.dataset.phoneRequiredMessage || 'Please enter a phone number to update your profile.';
                            phoneErrorEl.textContent = requiredMsg;
                            phoneErrorEl.classList.remove('tw-hidden');
                            phoneErrorEl.classList.add('po-error-text');
                            phoneErrorEl.dataset.errorMessage = requiredMsg;
                        }
                        if (phoneField) {
                            phoneField.classList.add('tw-error-border');
                            phoneField.setAttribute('aria-invalid', 'true');
                            phoneField.closest('po-text-field')?.classList.add('tw-po-textfield-error');
                            const nestedErr = phoneField.closest('po-text-field')?.querySelector('.tw-error-message');
                            if (nestedErr) {
                                nestedErr.classList.remove('tw-hidden');
                                nestedErr.classList.add('po-error-text');
                            }
                        }
                        isPhoneValid = false;
                    } else {
                        isPhoneValid = this.validateField(phoneField, this.isValidPhone, phoneErrorEl);
                    }

                    if (!isPhoneValid) return;

                    if (phoneField && !phoneField.hasEventListener) {
                        phoneField.addEventListener('input', () => {
                                if (this.hasSubmitted) {
                                    this.validateField(phoneField, this.isValidPhone, phoneErrorEl);
                                }
                        });
                        phoneField.hasEventListener = true;
                    }

          // detect if email or phone changed (support both field name variants)
          const newEmail = formFields.email || formFields['customer[email]'] || null;
          const emailChanged = this.originalEmail && newEmail && this.originalEmail.trim() !== newEmail.trim();
          const newPhone = formFields.phone || formFields['customer[phone]'] || null;
          const phoneChanged = this.originalPhone && newPhone && this.originalPhone.trim() !== newPhone.trim();

          if (!this.processing) {
              this.setProcessing(true);

              Promise.all([
                  this.updateCustomer(formFields, customerAccessToken),
                  this.updateAddress(formFields, customerAccessToken),
                  this.updateSmsConsent(formFields, customerAccessToken)
              ]).then(() => {
                  this.setProcessing(false);

                  if (emailChanged) {
                      // clear any stored tokens and redirect to canonical login path
                      try { sessionStorage.removeItem('customerAccessToken'); } catch (e) {}
                      try { localStorage.removeItem('customerAccessToken'); } catch (e) {}

                      try {
                          // Use the login route provided by the wrapper (data-login-route) when available
                          // to avoid hardcoding environment-specific URLs in source code.
                          const loginBase = this.dataset.loginRoute || (window.location.origin + '/account/login');
                          document.location.href = loginBase;
                      } catch (e) {
                          document.location.href = '/account/login';
                      }

                      return;
                  }

                  // If phone changed, reload page so server-rendered account summary reflects the new phone
                  if (phoneChanged) {
                      try {
                          window.location.reload();
                      } catch (e) {
                          if (this.actionUrl) document.location.href = this.actionUrl;
                      }
                      return;
                  }

                  if (this.actionUrl) {
                      document.location.href = this.actionUrl;
                  } else if (this.form.action) {
                      this.form.submit();
                  }
              }).catch((err) => {
                  this.setProcessing(false);
                  console.error('Error updating account:', err);

                  // Try to parse structured errors thrown by updateCustomer/updateAddress
                  const phoneErrorEl = this.form.querySelector('#phone-error');
                  let messages = [];

                  try {
                      // err may be an Error whose message is a JSON string we threw earlier
                      let parsed = null;

                      if (typeof err === 'string') {
                          parsed = JSON.parse(err);
                      } else if (err && err.message) {
                          try { parsed = JSON.parse(err.message); } catch (e) { parsed = err; }
                      } else {
                          parsed = err;
                      }

                      if (parsed) {
                          if (parsed.errors && Array.isArray(parsed.errors)) {
                              parsed.errors.forEach(e => {
                                  if (e && e.message) messages.push(e.message);
                                  else messages.push(JSON.stringify(e));
                              });
                          } else if (Array.isArray(parsed)) {
                              parsed.forEach(e => messages.push(e.message || JSON.stringify(e)));
                          } else if (parsed.message) {
                              messages.push(parsed.message);
                          }
                      }
                  } catch (e) {
                      messages.push(String(err));
                  }

                  if (messages.length > 0) {
                      const text = messages.join('; ');
                      const formErrorEl = this.form.querySelector('#account-form-error');
                      if (formErrorEl) {
                          formErrorEl.textContent = text;
                          formErrorEl.classList.remove('tw-hidden');
                      } else {
                          try { alert(text); } catch (e) { /* noop */ }
                      }
                      // Keep phone-error only for phone validation; clear it when showing API/email errors
                      if (phoneErrorEl) {
                          phoneErrorEl.textContent = '';
                          phoneErrorEl.classList.add('tw-hidden');
                          phoneErrorEl.classList.remove('po-error-text');
                      }
                  }
              });
          }
      }

      async getDefaultAddressId (fields, customerAccessToken) {
          const id = "gid://shopify/MailingAddress/" + fields.default_address_id;

          try {
              const response = await fetch(this.apiUrl, {
                  method: "POST",
                  headers: {
                      'Content-Type': 'application/json',
                      'X-Shopify-Storefront-Access-Token': this.apiToken
                  },
                  body: JSON.stringify({
                      query: `#graphql
                               query getCustomer($customerAccessToken: String!) {
                                  customer(customerAccessToken: $customerAccessToken) {
                                      id
                                      firstName
                                      lastName
                                      acceptsMarketing
                                      email
                                      phone
                                      addresses (first: 250) {
                                          edges {
                                              node {
                                                  id
                                              }
                                          }
                                      }
                                  }
                              }
                          `,
                          variables: { customerAccessToken }
                      })
              });

              const result = await response.json();
              const addresses = result.data?.customer?.addresses?.edges || [];

              if (result.error) {
                  console.error(result.error);
              }

              for (const address of addresses) {
                  if (address.node.id.includes(id)) {
                      return address.node.id;
                  }
              }
          } catch (error) {
              console.error(error);
          }

          return null;
      }

      async updateCustomer (fields, customerAccessToken) {
          // Usa sempre il valore scelto nel form per consentire l'aggiornamento
          // del marketing consent anche per utenti non EMEA (incluso US).
          let acceptsMarketing = !!fields["customer[accepts_marketing]"];
          
          // Salva la preferenza attuale per uso futuro
          localStorage.setItem('user_marketing_preference', acceptsMarketing);

          const formEmail = (fields.email || fields["customer[email]"] || "").trim();
          const originalEmail = (this.originalEmail || "").trim();
          const normalizedFormEmail = formEmail.toLowerCase();
          const normalizedOriginalEmail = originalEmail.toLowerCase();
          const shouldSendEmail = formEmail !== "" && normalizedFormEmail !== normalizedOriginalEmail;

          const formPhone = (fields.phone || fields["customer[phone]"] || "").trim();
          const originalPhone = (this.originalPhone || "").trim();
          const normalizedFormPhone = formPhone.replace(/\s+/g, "");
          const normalizedOriginalPhone = originalPhone.replace(/\s+/g, "");
          const shouldSendPhone = formPhone !== "" && normalizedFormPhone !== normalizedOriginalPhone;
          
          const customer = {
              "acceptsMarketing": acceptsMarketing,
              "firstName": fields.first_name,
              "lastName": fields.last_name,
          };

          // Evita errori "Email has already been taken" quando l'email non è stata realmente modificata.
          if (shouldSendEmail) {
              customer.email = formEmail;
          }

          // Evita errori "Phone has already been taken" quando il telefono non è stato realmente modificato.
          if (shouldSendPhone) {
              customer.phone = formPhone;
          }

          try {
              const response = await fetch(this.apiUrl, {
                  method: "POST",
                  headers: {
                      'Content-Type': 'application/json',
                      'X-Shopify-Storefront-Access-Token': this.apiToken
                  },
                  body: JSON.stringify({
                      query: `#graphql
                          mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
                              customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
                                  customer {
                                      id
                                  }
                                  customerUserErrors {
                                      code
                                      field
                                      message
                                  }
                              }
                          }
                      `,
                      variables: { customer, customerAccessToken }
                  })
              });

              const result = await response.json();

              // If Shopify returned customer user errors, surface them by rejecting
              const userErrors = result?.data?.customerUpdate?.customerUserErrors || [];
              if (userErrors.length > 0) {
                  // Throw a stringified error so the caller can parse and display messages
                  throw new Error(JSON.stringify({ source: 'customerUpdate', errors: userErrors }));
              }

              if (result.error) {
                  // general GraphQL errors
                  throw new Error(JSON.stringify({ source: 'graphql', errors: result.error }));
              }

              return result;
          }
          catch (error) {
              // Propagate the error so submitHandler can handle UI feedback
              throw error;
          }
      }

      async updateAddress (fields, customerAccessToken) {
          const id = await this.getDefaultAddressId(fields, customerAccessToken);

          const address = {
              "address1": fields.address_1,
              "address2": fields.address_2 || null,
              "city": fields.city,
              "country": fields.country,
              "firstName": fields.first_name,
              "lastName": fields.last_name,
              "phone": fields.phone,
              "province": fields.state,
              "zip": fields.zip
          }

          try {
              const response = await fetch(this.apiUrl, {
                  method: "POST",
                  headers: {
                      'Content-Type': 'application/json',
                      'X-Shopify-Storefront-Access-Token': this.apiToken
                  },
                  body: JSON.stringify({
                      query: `#graphql
                          mutation customerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
                              customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id) {
                                  customerAddress {
                                      address1
                                  }
                                  customerUserErrors {
                                      code
                                      field
                                      message
                                  }
                                  userErrors {
                                      field
                                      message
                                  }
                              }
                          }
                      `,
                      variables: { address, customerAccessToken, id }
                  })
              });
              const result = await response.json();

              const cuErrors = result?.data?.customerAddressUpdate?.customerUserErrors || [];
              const uErrors = result?.data?.customerAddressUpdate?.userErrors || [];

              const allErrors = [...cuErrors, ...uErrors];
              if (allErrors.length > 0) {
                  throw new Error(JSON.stringify({ source: 'customerAddressUpdate', errors: allErrors }));
              }

              if (result.error) {
                  throw new Error(JSON.stringify({ source: 'graphql', errors: result.error }));
              }

              return result;
          }
          catch (error) {
              throw error;
          }
      }

      getCustomerTags (fields) {
          const tags = {
              "birth": fields.birthday,
          }

          return Object.keys(tags).map(key => `${key}:${tags[key]}`).join(",");
      }

      async updateSmsConsent(fields, token, options = {}) {
          // Leggi il boomiUrl e brand dal data attribute del wrapper
          const boomiUrl = this.dataset?.boomiEnv;
          const brandName = this.dataset?.brandName || 'PO';
          
          // Verifica che abbiamo i dati necessari
          if (!fields.customer_gid) {
              console.warn("[updateSmsConsent] customer_gid mancante");
              return;
          }
          
          if (!boomiUrl) {
              console.warn("[updateSmsConsent] boomiUrl non trovato in data-boomi-env");
              return;
          }
          
          const hasPhoneField = Object.prototype.hasOwnProperty.call(fields, "phone")
              || Object.prototype.hasOwnProperty.call(fields, "customer[phone]");
          const normalizedPhone = this.normalizePhone(fields.phone || fields["customer[phone]"]);
          const normalizedOriginalPhone = this.normalizePhone(this.originalPhone);
          const phoneChanged = hasPhoneField && normalizedPhone !== normalizedOriginalPhone;

          // Leggi i consensi checkbox supportando sia payload FormData ("on"/undefined)
          // sia payload boolean già normalizzati.
          const smsConsentRaw = fields.sms_consent;
          const smsConsentValue = this.toBooleanConsent(smsConsentRaw);
          const smsConsentChanged = smsConsentValue !== this.originalSmsConsent;

          const mailMarketingRaw = typeof fields["customer[accepts_marketing]"] !== "undefined"
              ? fields["customer[accepts_marketing]"]
              : fields.accepts_marketing_email;
          const mailMarketingConsentValue = this.toBooleanConsent(mailMarketingRaw);
          const mailMarketingChanged = mailMarketingConsentValue !== this.originalMailMarketingConsent;

          const hasBirthdayField = Object.prototype.hasOwnProperty.call(fields, "birthday");
          const normalizedBirthday = this.normalizeDate(fields.birthday);
          const normalizedOriginalBirthday = this.normalizeDate(this.originalBirthday);
          const birthdayChanged = hasBirthdayField && normalizedBirthday !== normalizedOriginalBirthday;

          const payload = {};
          const customerUpdateInput = { id: fields.customer_gid };
          const metafields = [];

          if (phoneChanged || options.forcePhone) {
              customerUpdateInput.phone = (fields.phone || fields["customer[phone]"] || null);
          }

          if (smsConsentChanged || options.forceSmsConsent) {
              metafields.push({
                  namespace: "custom",
                  key: "sms_consent",
                  type: "boolean",
                  value: smsConsentValue
              });
          }

          if (mailMarketingChanged || options.forceMailMarketingConsent) {
              metafields.push({
                  namespace: "custom",
                  key: "mail_marketing_consent",
                  type: "boolean",
                  value: mailMarketingConsentValue
              });
          }

          if (birthdayChanged || options.forceBirthDate) {
              metafields.push({
                  namespace: "facts",
                  key: "birth_date",
                  type: "date",
                  value: normalizedBirthday || null
              });
          }

          if (metafields.length > 0) {
              customerUpdateInput.metafields = metafields;
          }

          if (Object.keys(customerUpdateInput).length > 1) {
              payload.customerUpdate = { input: customerUpdateInput };
          }

          if (smsConsentChanged || options.forceSmsMarketingConsentUpdate) {
              payload.customerSmsMarketingConsentUpdate = {
                  input: {
                      id: fields.customer_gid,
                      smsMarketingConsent: {
                          marketingState: smsConsentValue ? "SUBSCRIBED" : "UNSUBSCRIBED",
                          marketingOptInLevel: "SINGLE_OPT_IN"
                      }
                  }
              };
          }

          if (Object.keys(payload).length === 0) {
              console.log('[updateSmsConsent] Nessuna modifica rilevata per Boomi, skip chiamata');
              return { skipped: true };
          }

          try {
              const response = await fetch(boomiUrl, {
                  method: 'POST',
                  headers: {
                      'Content-Type': 'application/json',
                      'Authentication': token,
                      'Brand': brandName
                  },
                  body: JSON.stringify(payload)
              });

              if (!response.ok) {
                  const errorText = await response.text();
                  console.error("[updateSmsConsent] Errore:", response.status, errorText);
                  return;
              }


              try {
                 var result= await response.json();
              } catch (success) {
                  console.log('[updateSmsConsent] success with no response body');
              }

              console.log('[updateSmsConsent] success:', result);
              return result;
          } catch (error) {
              console.error("[updateSmsConsent] Errore fetch:", error);
          }
      }
      
      setProcessing (status) {
          this.processing = status;
          this.submitButtons.forEach(btn => btn.disabled = status);
      }
  });
}
